import {
  json,
  type LoaderFunctionArgs,
  type ActionFunctionArgs,
} from '@remix-run/node';
import { useLoaderData } from '@remix-run/react';
import { useTranslation } from 'react-i18next';
import { CollectionCard } from '~/components/collections/CollectionCard';
import { CollectionList } from '~/components/collections/CollectionList';
import { FAQSection } from '~/components/faqs/FAQSection';
import HeroSection from '~/components/hero-section/HeroSection';
import ProductGrid from '~/components/products/ProductGrid';
import { WhyVapeStore } from '~/components/testimonials/WhyVapeStore';
import Newsletter from '~/components/newsletter/NewsletterSection';
import type {
  ListedProductFragment,
  SearchQueryVariables,
} from '~/generated/graphql';
import { getCollections } from '~/providers/collections/collections';
import { search } from '~/providers/products/products';
import { activeBanner } from '~/providers/banner/banner';
import { subscribeToNewsletter } from '~/providers/newsletter/newsletter';
import { AccordionFaqs } from '~/providers/faqs/faqs';


type Collection = Awaited<ReturnType<typeof getCollections>>[number];

type LoaderData = {
  collections: Collection[];
  products: ListedProductFragment[];
  banner: Awaited<ReturnType<typeof activeBanner>> | null;
  faqs: NonNullable<Awaited<ReturnType<typeof AccordionFaqs>>>;
};

export async function loader({ request }: LoaderFunctionArgs) {
  const searchInput: SearchQueryVariables['input'] = { take: 10, skip: 0 };

  const [collectionsRes, searchRes, banner] = await Promise.all([
    getCollections(request, { take: 20 }, { take: 5 }),
    search({ input: searchInput }, { request }),
    activeBanner({ request }),
  ]);

  const listedProducts: ListedProductFragment[] = searchRes.search?.items || [];

  const faqs = (await AccordionFaqs({ request })) || [];

  return json<LoaderData>({
    collections: collectionsRes,
    products: listedProducts,
    banner,
    faqs,
  });
}

export async function action({ request }: ActionFunctionArgs) {
  const formData = await request.formData();
  const email = formData.get('email') as string;
  const name = formData.get('name') as string;

  try {
    const res = await subscribeToNewsletter({ email, name });
    return json({ success: res.success });
  } catch (error) {
    console.error(error);
    return json({ success: false });
  }
}

export default function Index() {
  const { collections, products, banner, faqs } = useLoaderData<LoaderData>();
  const { t } = useTranslation();

  return (
    <section className="container">
      <div className="relative">
        <HeroSection banner={banner} />

        {/* Featured Products */}
        <div className="px-4 sm:px-6 lg:px-8 xl:px-0 mt-10">
          <h2 className="lg:text-4xl text-3xl font-semibold px-3 lg:px-16 py-3 lg:py-8 tracking-tight text-primary">
            {t('common.featuredProducts')}
          </h2>
          <ProductGrid products={products} />
        </div>
      </div>

      {/* Shop by Category */}
      <section
        aria-labelledby="category-heading"
        className="pt-8 mt-12 mb-8 rounded-xl xl:max-w-7xl xl:mx-auto xl:px-8 bg-[#8181810A]"
      >
        <div className="px-4 text-center sm:px-6 lg:px-8 xl:px-0">
          <h2
            id="category-heading"
            className="text-3xl font-semibold tracking-tight text-gray-900"
          >
            {t('common.shopByCategory')}
          </h2>
          <p className="mt-2 text-black text-base max-w-3xl pt-2 pb-8 text-center mx-auto">
            {t('categories.description')}
          </p>
        </div>

        <div className="mt-4 flow-root">
          <div className="-my-2">
            <div className="box-content pb-8 px-2 relative overflow-x-auto xl:overflow-visible">
              <div className="grid justify-items-center grid-cols-1 md:grid-cols-4 gap-y-8 gap-x-8 sm:px-6 lg:px-8 xl:relative xl:px-0 xl:space-x-0 xl:gap-x-8">
                {collections.slice(0, 4).map((collection: Collection) => (
                  <CollectionCard key={collection.id} collection={collection} />
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Full collection list */}
      <div className="max-w-[5000px] mx-auto px-3 lg:px-10 py-6 space-y-6">
        <CollectionList collections={collections} />
      </div>

      {/* FAQ Section */}
      <FAQSection faqs={faqs} />

      <WhyVapeStore />
      <Newsletter />
    </section>
  );
}
